import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Integration, IntegrationType } from '@pdqdev/pdq-shared/dist/entities';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '@core/services/api/api.service';
import { IntegrationsService } from '@services/integrations/integrations.service';
import { ToastService } from '@storybook/services/toast/toast.service';

@Component({
  selector: 'triple-whale-settings',
  templateUrl: './triple-whale-settings.component.html',
  styleUrls: ['./triple-whale-settings.component.scss'],
  standalone: false,
})
export class TripleWhaleSettingsComponent implements OnInit, OnDestroy {
  @Input() integrationType: IntegrationType;
  @Input() integration: Integration | any;

  apiKeyControl = new FormControl('');
  sendShippingCosts: boolean = false;
  savedSendShippingCosts: boolean = false;
  loading: boolean = false;
  savingSettings: boolean = false;
  disconnecting: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private apiService: ApiService,
    private integrationService: IntegrationsService,
    private toastService: ToastService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.integrationService.integrations$.pipe(takeUntil(this.destroy$)).subscribe((integrations) => {
      this.integration = integrations[this.integrationType.name]?.instance;
      this.initSettings();
    });
  }

  initSettings() {
    if (this.integration?.metadata) {
      this.sendShippingCosts = this.integration.metadata.send_shipping_costs ?? false;
      this.savedSendShippingCosts = this.sendShippingCosts;
    }
  }

  connect() {
    this.loading = true;
    this.apiService
      .post('integrations/triplewhale/connect', {
        apiKey: this.apiKeyControl.value,
        sendShippingCosts: this.sendShippingCosts,
      })
      .subscribe({
        next: (result: any) => {
          this.integrationService.updateIntegrationState(result.integration);
          this.toastService.showToast({ mode: 'success', message: 'Connected to Triple Whale' });
          this.apiKeyControl.reset();
          this.loading = false;
        },
        error: (err) => {
          this.toastService.showToast({
            mode: 'danger',
            message: err?.error?.error || 'Failed to connect. Check your API key.',
          });
          this.loading = false;
        },
      });
  }

  disconnect() {
    this.disconnecting = true;
    this.apiService.post('integrations/triplewhale/disconnect').subscribe({
      next: (result: any) => {
        this.integrationService.updateIntegrationState(result.integration);
        this.toastService.showToast({ mode: 'success', message: 'Disconnected from Triple Whale' });
        this.disconnecting = false;
      },
      error: () => {
        this.toastService.showToast({ mode: 'danger', message: 'Failed to disconnect' });
        this.disconnecting = false;
      },
    });
  }

  saveSettings() {
    this.savingSettings = true;
    this.apiService
      .post(`integrations/metadata/${this.integration?.id}`, {
        key: 'send_shipping_costs',
        value: this.sendShippingCosts,
      })
      .subscribe({
        next: (updatedIntegration) => {
          this.integrationService.updateIntegrationState(updatedIntegration);
          this.toastService.showToast({ mode: 'success', message: 'Settings saved' });
          this.savingSettings = false;
        },
        error: () => {
          this.toastService.showToast({ mode: 'danger', message: 'Failed to save settings' });
          this.savingSettings = false;
        },
      });
  }
}
